import React, { useState } from 'react';
import MarkdownRenderer from './MarkdownRenderer';
import bedrockService from '../services/bedrockService';

/**
 * Renders a single assistant message in the Document Chat interface.
 *
 * Displays the AI-generated answer with markdown formatting, a collapsible
 * list of source documents, and action buttons for copying to clipboard,
 * exporting to PDF, and exporting to DOCX.
 *
 * @param {Object}   props
 * @param {string}   props.answer   - The Bedrock-generated answer text.
 * @param {string[]} props.sources  - Filenames provided as context for this answer.
 * @param {number}   props.index    - Message index used to key the component.
 */
const DocChatMessage = ({ answer, sources = [], index }) => {
  const [copied, setCopied] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingDoc, setExportingDoc] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);

  // ------------------------------------------------------------------
  // Clipboard
  // ------------------------------------------------------------------
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(answer);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for browsers that block clipboard without HTTPS
      const textarea = document.createElement('textarea');
      textarea.value = answer;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // ------------------------------------------------------------------
  // Shared async export helper
  // ------------------------------------------------------------------
  const handleExport = async (format, setExporting) => {
    setExporting(true);
    try {
      const { job_id } = await bedrockService.submitDocChatExport(answer, sources, format, index + 1);

      const POLL_INTERVAL_MS = 2000;
      const MAX_WAIT_MS = 120000;
      const deadline = Date.now() + MAX_WAIT_MS;

      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        const statusData = await bedrockService.getDocChatExportStatus(job_id);
        if (statusData.status === 'completed') {
          // Trigger the download via a temporary anchor
          const a = document.createElement('a');
          a.href = statusData.download_url;
          a.download = statusData.filename || `doc-chat-response-${index + 1}.${format}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          return;
        }
        if (statusData.status === 'failed') {
          throw new Error(statusData.error || 'Export failed');
        }
      }
      throw new Error('Export timed out. Please try again.');
    } catch (err) {
      console.error('Export error:', err);
      alert(`Export failed: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <div className="card mb-3 border-0 shadow-sm">
      <div className="card-body p-3">
        {/* Answer */}
        <div className="doc-chat-answer mb-3">
          <MarkdownRenderer content={answer} />
        </div>

        {/* Sources (collapsible) */}
        {sources.length > 0 && (
          <div className="mb-3">
            <button
              className="btn btn-link btn-sm p-0 text-secondary text-decoration-none"
              onClick={() => setSourcesOpen((o) => !o)}
              aria-expanded={sourcesOpen}
            >
              <i className={`bi bi-chevron-${sourcesOpen ? 'up' : 'down'} me-1`} />
              {sources.length === 1
                ? '1 source document'
                : `${sources.length} source documents`}
            </button>

            {sourcesOpen && (
              <ul className="list-unstyled mt-2 mb-0 ps-2">
                {sources.map((src) => (
                  <li key={src} className="d-flex align-items-center gap-1 small text-muted mb-1">
                    <i className="bi bi-file-earmark-text" />
                    {src}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="d-flex gap-2 flex-wrap">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={handleCopy}
            title="Copy answer to clipboard"
          >
            <i className={`bi ${copied ? 'bi-check2' : 'bi-clipboard'} me-1`} />
            {copied ? 'Copied!' : 'Copy'}
          </button>

          <button
            className="btn btn-outline-danger btn-sm"
            onClick={() => handleExport('pdf', setExportingPdf)}
            disabled={exportingPdf}
            title="Export this response as a PDF"
          >
            {exportingPdf ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                Exporting…
              </>
            ) : (
              <>
                <i className="bi bi-file-earmark-pdf me-1" />
                Export PDF
              </>
            )}
          </button>

          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => handleExport('docx', setExportingDoc)}
            disabled={exportingDoc}
            title="Export this response as a Word document"
          >
            {exportingDoc ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                Exporting…
              </>
            ) : (
              <>
                <i className="bi bi-file-earmark-word me-1" />
                Export DOCX
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocChatMessage;
